import AdStatusButtons from '../../components/AdStatusButtons';
import AdStatusToggle from '../../components/AdStatusToggle';
import { useAdStatus, useMayChangeStatus, NOUN } from '../../components/ad-status';
import { resolveSelectedIds } from '../../utils/entity-list';
import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  ContentCopy,
  Delete,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adsService, Ad } from '../../services/ads.service';
import { adsetsService } from '../../services/adsets.service';
import StatusBadge from '../../components/StatusBadge';
import EntityListFilters, { type EntityFilters } from '../../components/EntityListFilters';
import CreativePreviewDialog from '../../components/CreativePreviewDialog';

export default function AdsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [filters, setFilters] = useState<EntityFilters>({ search: '', status: '', adAccountId: '' });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>({ type: 'include', ids: new Set<string>() });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuAd, setMenuAd] = useState<Ad | null>(null);

  const adsetId = searchParams.get('adsetId') || '';

  const { data: adsets } = useQuery({
    queryKey: ['adsets'],
    queryFn: () => adsetsService.query({ limit: 100 }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ads', adsetId, page, pageSize, filters],
    queryFn: () => adsService.query({
      ad_set_id: adsetId || undefined,
      offset: page * pageSize,
      limit: pageSize,
      ...(filters.search && { search: filters.search }),
      ...(filters.status && { status: filters.status }),
      ...(filters.adAccountId && { ad_account_id: filters.adAccountId }),
    }),
  });

  const handleFiltersChange = (next: EntityFilters) => {
    setFilters(next);
    setPage(0);
  };

  const deleteMutation = useMutation({
    mutationFn: adsService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => adsService.duplicate(id),
    onSuccess: (newAd) => {
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      navigate(`/ads/${newAd.id}/edit`);
    },
  });

  const [previewAdId, setPreviewAdId] = useState<string | null>(null);

  const mayChangeStatus = useMayChangeStatus();
  const rowStatus = useAdStatus('ad', 'ads');

  const columns: GridColDef[] = [
    {
      field: 'creative_image_url',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const url = params.value;
        const handleClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          setPreviewAdId(params.row.id);
        };
        if (!url) return <Box onClick={handleClick} sx={{ width: 40, height: 40, bgcolor: 'action.hover', borderRadius: 1, cursor: 'pointer' }} />;
        return (
          <Box
            component="img"
            src={url}
            alt=""
            onClick={handleClick}
            sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
            onError={(e: any) => { e.target.style.display = 'none'; }}
          />
        );
      },
    },
    {
      field: 'name',
      headerName: 'Ad Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'ad_set',
      headerName: 'Ad Set',
      width: 180,
      valueGetter: (_value, row) => row.ad_set?.name || '-',
    },
    {
      field: 'campaign',
      headerName: 'Campaign',
      width: 180,
      valueGetter: (_value, row) => row.campaign?.name || '-',
    },
    {
      field: 'ad_account',
      headerName: 'Ad Account',
      width: 180,
      valueGetter: (_value, row) => row.ad_account?.name || '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <StatusBadge
            status={params.value}
            effectiveStatus={params.row.effective_status}
            staleSince={params.row.status_stale_since}
          />
          {mayChangeStatus && (
            <AdStatusToggle
              status={params.value}
              noun={NOUN['ad'][0]}
              onToggle={(next) => rowStatus.ask([String(params.row.id)], next)}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'headline',
      headerName: 'Headline',
      width: 200,
      valueFormatter: (value) => value || '-',
    },
    {
      field: 'call_to_action',
      headerName: 'CTA',
      width: 130,
      valueFormatter: (value: string | null) => value?.replace('_', ' ') || '-',
    },
    {
      field: 'link_url',
      headerName: 'Link URL',
      width: 200,
      renderCell: (params: GridRenderCellParams) => params.value ? (
        <Typography
          variant="body2"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={params.value}
        >
          {params.value}
        </Typography>
      ) : '-',
    },
    {
      field: 'fb_ad_id',
      headerName: 'FB ID',
      width: 160,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return <Typography variant="caption" color="text.disabled">-</Typography>;
        return (
          <Tooltip title="Click to copy">
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', fontSize: '0.7rem', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(params.value); }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) =>
        new Date(value).toLocaleDateString(),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuAd(params.row);
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const rows = data?.data || [];
  const selectedIds = resolveSelectedIds(selectedRows, rows);
  const totalsRow = useMemo(() => {
    if (rows.length === 0) return null;
    return {
      id: '__totals__',
      name: `Total (${data?.total || rows.length})`,
      creative_image_url: null,
      ad_set: null,
      campaign: null,
      ad_account: null,
      status: '',
      headline: '',
      call_to_action: '',
      link_url: '',
      fb_ad_id: null,
      created_at: '',
    };
  }, [rows, data?.total]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Ads
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedIds.length > 0 && (
            <AdStatusButtons
              entityType="ad"
              ids={selectedIds}
              invalidateKey="ads"
              alreadyInState={(status) => selectedIds.filter(
                (id) => rows.find((r) => String(r.id) === id)?.status === status).length}
              onDone={() => setSelectedRows({ type: 'include', ids: new Set() })}
            />
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/ads/new')}
          >
            Create Ad
          </Button>
        </Box>
      </Box>

      <EntityListFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        extraFilters={
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={adsets?.data || []}
            getOptionLabel={(o) => o.name}
            value={adsets?.data?.find(a => a.id === adsetId) || null}
            onChange={(_, v) => {
              if (v) {
                setSearchParams({ adsetId: v.id });
              } else {
                setSearchParams({});
              }
              setPage(0);
            }}
            renderInput={(params) => <TextField {...params} label="Ad Set" placeholder="All ad sets" />}
          />
        }
      />

      <DataGridPremium
        rows={rows}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total || 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[50, 100, 200, 400]}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={setSelectedRows}
        disableRowSelectionOnClick
        pinnedRows={totalsRow ? { top: [totalsRow] } : undefined}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          height: 'calc(100vh - 280px)',
          minHeight: 400,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: 'pointer',
          },
          '& .MuiDataGrid-pinnedRows': {
            bgcolor: 'action.hover',
            fontWeight: 600,
          },
        }}
        onRowClick={(params: GridRowParams) => {
          if (params.id === '__totals__') return;
          navigate(`/ads/${params.id}`);
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            navigate(`/ads/${menuAd?.id}/edit`);
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAd) {
              duplicateMutation.mutate(menuAd.id);
            }
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAd) {
              deleteMutation.mutate(menuAd.id);
            }
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      <CreativePreviewDialog
        open={!!previewAdId}
        onClose={() => setPreviewAdId(null)}
        adId={previewAdId || ''}
      />

      {rowStatus.dialogs}
    </Box>
  );
}
